import { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../api';

function Btn({ onClick, children, variant='primary', small, disabled, style={} }) {
  const s={primary:{background:'#c9a96e',color:'#1a1208',border:'none'},secondary:{background:'transparent',color:'#8a7055',border:'1px solid #3a2a12'},danger:{background:'#e63c3c22',color:'#e63c3c',border:'1px solid #e63c3c44'},success:{background:'#10b98122',color:'#10b981',border:'1px solid #10b98144'},ghost:{background:'#c9a96e18',color:'#c9a96e',border:'1px solid #c9a96e44'}};
  return <button onClick={onClick} disabled={disabled} style={{...s[variant],borderRadius:8,padding:small?'6px 14px':'10px 22px',fontWeight:700,cursor:disabled?'default':'pointer',fontSize:small?12:14,opacity:disabled?0.5:1,fontFamily:'inherit',...style}}>{children}</button>;
}
function Modal({ children, onClose, wide }) {
  return (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}><div style={{background:'#1a1208',border:'1px solid #3a2a12',borderRadius:14,padding:32,maxWidth:wide?860:560,width:'100%',maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>{children}</div></div>);
}

const typeColor = { cold:'#6366f1', freezer:'#3b82f6', hot:'#e63c3c' };
const typeLabel = { cold:'❄️ Cold', freezer:'🧊 Freezer', hot:'🔥 Hot Hold' };
const statusColor = { ok:'#10b981', warning:'#f59e0b', critical:'#e63c3c', none:'#8a7055' };
const statusLabel = { ok:'✓ In Range', warning:'⚠ Warning', critical:'🚨 Critical', none:'No Data' };

export function TempLogger({ isManager }) {
  const [units, setUnits] = useState([]);
  const [summary, setSummary] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState('log');
  const [logModal, setLogModal] = useState(null);
  const [addUnitModal, setAddUnitModal] = useState(false);
  const [editUnit, setEditUnit] = useState(null);
  const [form, setForm] = useState({ temperature:'', notes:'', shift:'opening' });
  const [unitForm, setUnitForm] = useState({ name:'', equipment_type:'cold', target_min:34, target_max:41, temp_unit:'F', check_frequency:'twice_daily', location:'' });
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const load = useCallback(async () => {
    const [u, l] = await Promise.all([get('/temp/equipment'), get('/temp/logs?days=7')]);
    setUnits(u);
    setLogs(l);
    if (isManager) {
      const s = await get('/temp/summary?days=30');
      setSummary(s);
    }
  }, [isManager]);
  useEffect(()=>{load();},[load]);

  async function submitLog() {
    if (!form.temperature) return;
    setSubmitting(true);
    try {
      const result = await post('/temp/logs', { equipment_id: logModal.id, temperature: parseFloat(form.temperature), notes: form.notes, shift: form.shift });
      setSuccessMsg(result.status === 'ok' ? `✓ ${result.unit_name}: ${form.temperature}°F — In range` : `⚠️ ${result.unit_name}: ${form.temperature}°F — OUT OF RANGE! Safe range: ${result.target_min}–${result.target_max}°F`);
      setForm({ temperature:'', notes:'', shift:'opening' });
      setLogModal(null);
      load();
    } catch(e){alert(e.message);}
    finally{setSubmitting(false);}
  }

  async function saveUnit() {
    if (!unitForm.name) return;
    try {
      if (editUnit) await put(`/temp/equipment/${editUnit.id}`, unitForm);
      else await post('/temp/equipment', unitForm);
      setAddUnitModal(false); setEditUnit(null);
      setUnitForm({ name:'', equipment_type:'cold', target_min:34, target_max:41, temp_unit:'F', check_frequency:'twice_daily', location:'' });
      load();
    } catch(e){alert(e.message);}
  }

  async function removeUnit(id) {
    if (!window.confirm('Remove this equipment unit?')) return;
    await del(`/temp/equipment/${id}`);
    load();
  }

  function getLastLog(unitId) { return logs.find(l=>l.equipment_id===unitId); }

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div><h2 style={{fontFamily:"'Playfair Display', serif",fontSize:26,color:'#f5e6c8',marginBottom:4}}>🌡️ Temperature Log</h2>
        <p style={{color:'#8a7055',fontSize:13}}>Log temperatures at opening, closing, and every 2 hours for hot hold</p></div>
        {isManager && <Btn small variant="ghost" onClick={()=>setAddUnitModal(true)}>+ Add Equipment</Btn>}
      </div>

      {successMsg && (
        <div style={{background:successMsg.includes('OUT OF RANGE')?'#e63c3c18':'#10b98118',border:`1px solid ${successMsg.includes('OUT OF RANGE')?'#e63c3c':'#10b981'}44`,borderRadius:10,padding:16,marginBottom:20}}>
          <div style={{color:successMsg.includes('OUT OF RANGE')?'#e63c3c':'#10b981',fontWeight:700,fontSize:14}}>{successMsg}</div>
          {successMsg.includes('OUT OF RANGE') && <div style={{color:'#d4b896',fontSize:13,marginTop:6}}>⚠️ Take corrective action immediately and notify a manager.</div>}
          <button onClick={()=>setSuccessMsg('')} style={{background:'none',border:'none',color:'#8a7055',cursor:'pointer',marginTop:8,fontFamily:'inherit',fontSize:12}}>Dismiss</button>
        </div>
      )}

      {/* Log temp modal */}
      {logModal && (
        <Modal onClose={()=>setLogModal(null)}>
          <h3 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',marginBottom:4}}>{logModal.name}</h3>
          <p style={{color:'#8a7055',fontSize:13,marginBottom:20}}>Safe range: {logModal.target_min}–{logModal.target_max}°{logModal.temp_unit} · {typeLabel[logModal.equipment_type]}</p>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Temperature (°{logModal.temp_unit})</label>
            <input type="number" step="0.1" value={form.temperature} onChange={e=>setForm(p=>({...p,temperature:e.target.value}))} placeholder={`e.g. ${logModal.target_min + 2}`}
              style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'14px',color:'#f5e6c8',fontSize:24,fontWeight:700,boxSizing:'border-box',textAlign:'center'}} autoFocus />
            <div style={{color:'#5a4030',fontSize:12,marginTop:6,textAlign:'center'}}>Safe range: {logModal.target_min}° – {logModal.target_max}°{logModal.temp_unit}</div>
          </div>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Shift</label>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
              {[{v:'opening',l:'Opening'},{v:'midday',l:'Mid-Shift'},{v:'closing',l:'Closing'}].map(o=>(
                <div key={o.v} onClick={()=>setForm(p=>({...p,shift:o.v}))} style={{padding:10,borderRadius:8,cursor:'pointer',border:`1px solid ${form.shift===o.v?'#c9a96e':'#3a2a12'}`,background:form.shift===o.v?'#c9a96e18':'#2a1f0f',color:form.shift===o.v?'#c9a96e':'#8a7055',textAlign:'center',fontSize:13}}>{o.l}</div>
              ))}
            </div>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Notes (optional)</label>
            <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Any observations..." style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',fontFamily:'inherit'}} />
          </div>
          <div style={{display:'flex',gap:10}}>
            <Btn onClick={submitLog} disabled={submitting||!form.temperature}>{submitting?'Logging...':'Submit Temperature'}</Btn>
            <Btn variant="secondary" onClick={()=>setLogModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Add/edit equipment modal */}
      {(addUnitModal||editUnit) && isManager && (
        <Modal onClose={()=>{setAddUnitModal(false);setEditUnit(null);}}>
          <h3 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',marginBottom:20}}>{editUnit?'Edit Equipment':'Add Equipment'}</h3>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Equipment Name</label>
            <input value={unitForm.name} onChange={e=>setUnitForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Walk-In Cooler" style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',fontFamily:'inherit'}} />
          </div>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Type</label>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
              {[{v:'cold',l:'❄️ Refrigerator'},{v:'freezer',l:'🧊 Freezer'},{v:'hot',l:'🔥 Hot Hold'}].map(o=>(
                <div key={o.v} onClick={()=>setUnitForm(p=>({...p,equipment_type:o.v}))} style={{padding:10,borderRadius:8,cursor:'pointer',border:`1px solid ${unitForm.equipment_type===o.v?typeColor[o.v]:'#3a2a12'}`,background:unitForm.equipment_type===o.v?typeColor[o.v]+'18':'#2a1f0f',color:unitForm.equipment_type===o.v?typeColor[o.v]:'#8a7055',textAlign:'center',fontSize:12}}>{o.l}</div>
              ))}
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            {[{l:'Min Safe Temp',k:'target_min'},{l:'Max Safe Temp',k:'target_max'}].map(f=>(
              <div key={f.k}>
                <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>{f.l}</label>
                <input type="number" step="1" value={unitForm[f.k]} onChange={e=>setUnitForm(p=>({...p,[f.k]:parseFloat(e.target.value)||0}))} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box'}} />
              </div>
            ))}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>
            <div><label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Temperature Unit</label>
              <select value={unitForm.temp_unit} onChange={e=>setUnitForm(p=>({...p,temp_unit:e.target.value}))} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,fontFamily:'inherit'}}>
                <option value="F">°F (Fahrenheit)</option><option value="C">°C (Celsius)</option>
              </select></div>
            <div><label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Location</label>
              <input value={unitForm.location} onChange={e=>setUnitForm(p=>({...p,location:e.target.value}))} placeholder="e.g. Kitchen, Bar" style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',fontFamily:'inherit'}} /></div>
          </div>
          <div style={{display:'flex',gap:10}}>
            <Btn onClick={saveUnit} disabled={!unitForm.name}>Save</Btn>
            <Btn variant="secondary" onClick={()=>{setAddUnitModal(false);setEditUnit(null);}}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:24,borderBottom:'1px solid #3a2a12'}}>
        {[{k:'log',l:'Log Temperature'},{k:'history',l:'Recent Logs'},{k:'summary',l:'Summary'}].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)} style={{background:'none',border:'none',borderBottom:tab===t.k?'2px solid #c9a96e':'2px solid transparent',color:tab===t.k?'#c9a96e':'#8a7055',padding:'8px 16px',cursor:'pointer',fontWeight:tab===t.k?700:400,fontSize:13,fontFamily:'inherit'}}>{t.l}</button>
        ))}
      </div>

      {tab === 'log' && (
        <div>
          <p style={{color:'#8a7055',fontSize:13,marginBottom:20}}>Select equipment to log the current temperature</p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:14}}>
            {units.map(u => {
              const lastLog = getLastLog(u.id);
              const cc = typeColor[u.equipment_type] || '#c9a96e';
              const sc = statusColor[lastLog?.status||'none'];
              return (
                <div key={u.id} style={{background:'#1a1208',border:`1px solid ${cc}44`,borderRadius:12,overflow:'hidden'}}>
                  <div style={{background:cc+'18',padding:'14px 16px',borderBottom:`1px solid ${cc}33`}}>
                    <div style={{color:cc,fontWeight:700,fontSize:12,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>{typeLabel[u.equipment_type]}</div>
                    <div style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',fontSize:18}}>{u.name}</div>
                    {u.location && <div style={{color:'#8a7055',fontSize:12,marginTop:2}}>{u.location}</div>}
                  </div>
                  <div style={{padding:'14px 16px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                      <div>
                        <div style={{color:'#8a7055',fontSize:11,marginBottom:2}}>Safe Range</div>
                        <div style={{color:'#f5e6c8',fontSize:13,fontWeight:600}}>{u.target_min}–{u.target_max}°{u.temp_unit}</div>
                      </div>
                      {lastLog && <div style={{textAlign:'right'}}>
                        <div style={{color:'#8a7055',fontSize:11,marginBottom:2}}>Last Reading</div>
                        <div style={{color:sc,fontSize:16,fontWeight:700}}>{lastLog.temperature}°{u.temp_unit}</div>
                        <div style={{color:sc,fontSize:10,fontWeight:600}}>{statusLabel[lastLog.status]}</div>
                      </div>}
                    </div>
                    {lastLog && <div style={{color:'#5a4030',fontSize:11,marginBottom:10}}>{new Date(lastLog.created_at).toLocaleString()}</div>}
                    <div style={{display:'flex',gap:8}}>
                      <Btn small onClick={()=>{setLogModal(u);setForm({temperature:'',notes:'',shift:'opening'});}} style={{flex:1}}>+ Log Temp</Btn>
                      {isManager && <Btn small variant="ghost" onClick={()=>{setEditUnit(u);setUnitForm({name:u.name,equipment_type:u.equipment_type,target_min:u.target_min,target_max:u.target_max,temp_unit:u.temp_unit,check_frequency:u.check_frequency,location:u.location||''});}}>✏️</Btn>}
                      {isManager && <Btn small variant="danger" onClick={()=>removeUnit(u.id)}>🗑</Btn>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {units.length===0 && <p style={{color:'#5a4030',textAlign:'center',padding:40}}>No equipment units. {isManager?'Click + Add Equipment to get started.':''}</p>}
        </div>
      )}

      {tab === 'history' && (
        <div>
          <p style={{color:'#8a7055',fontSize:13,marginBottom:16}}>Temperature logs from the last 7 days</p>
          <div style={{display:'grid',gap:8}}>
            {logs.map(l=>(
              <div key={l.id} style={{background:'#1a1208',border:`1px solid ${statusColor[l.status]}33`,borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',gap:14,borderLeft:`4px solid ${statusColor[l.status]}`}}>
                <div style={{flex:1}}>
                  <div style={{color:'#f5e6c8',fontWeight:600,fontSize:13}}>{l.equipment_name}</div>
                  <div style={{color:'#5a4030',fontSize:11}}>{new Date(l.created_at).toLocaleString()} · {l.logged_by_name} · {l.shift}</div>
                  {l.notes && <div style={{color:'#8a7055',fontSize:12,marginTop:2}}>{l.notes}</div>}
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div style={{color:statusColor[l.status],fontWeight:700,fontSize:20}}>{l.temperature}°{l.temp_unit}</div>
                  <div style={{color:statusColor[l.status],fontSize:11,fontWeight:600}}>{statusLabel[l.status]}</div>
                </div>
              </div>
            ))}
            {logs.length===0 && <p style={{color:'#5a4030',textAlign:'center',padding:40}}>No temperature logs yet.</p>}
          </div>
        </div>
      )}

      {tab === 'summary' && isManager && (
        <div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <p style={{color:'#8a7055',fontSize:13}}>30-day equipment summary</p>
            <a href="/api/temp/export?days=30" target="_blank" style={{background:'#6366f122',color:'#6366f1',border:'1px solid #6366f144',borderRadius:8,padding:'6px 14px',textDecoration:'none',fontSize:12,fontWeight:700}}>📊 Export CSV</a>
          </div>
          <div style={{display:'grid',gap:12}}>
            {summary.map(u=>{
              const cc = typeColor[u.equipment_type]||'#c9a96e';
              const sc = statusColor[u.last_status||'none'];
              return (
                <div key={u.id} style={{background:'#1a1208',border:`1px solid ${u.warnings>0?'#f59e0b33':'#3a2a12'}`,borderRadius:10,padding:'16px 20px',display:'flex',alignItems:'center',gap:16}}>
                  <div style={{width:12,height:12,borderRadius:'50%',background:cc,flexShrink:0}} />
                  <div style={{flex:1}}>
                    <div style={{color:'#f5e6c8',fontWeight:600}}>{u.name}</div>
                    <div style={{color:'#5a4030',fontSize:12}}>{u.location} · Safe: {u.target_min}–{u.target_max}°{u.temp_unit}</div>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <div style={{color:'#f5e6c8',fontWeight:700}}>{u.log_count}</div>
                    <div style={{color:'#5a4030',fontSize:11}}>Logs</div>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <div style={{color:u.warnings>0?'#f59e0b':'#10b981',fontWeight:700}}>{u.warnings}</div>
                    <div style={{color:'#5a4030',fontSize:11}}>Warnings</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{color:sc,fontWeight:700,fontSize:16}}>{u.last_temp?`${u.last_temp}°${u.temp_unit}`:'No data'}</div>
                    <div style={{color:'#5a4030',fontSize:11}}>{u.last_logged?new Date(u.last_logged).toLocaleDateString():'Never logged'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
